import React, { useState, useEffect, isValidElement } from 'react';
import Image from '@components/shared/Image';
import useBreakpoints from '@hooks/useBreakpoints';
import useTheme from '@hooks/useTheme';
import { useTranslate } from 'nucleus-text';
import { injectTestId } from '@cvent/nucleus-test-automation';
import PropTypes from 'prop-types';
import { useThemeValues } from '@context/ThemeProvider';
import Time from '@images/time.svg';
import { isApptNotificationChannel } from '@components/notifications/utils';
import Link from 'next/link';
import { TextLink } from '@cvent/carina/components/TextLink';
import Icon from '@components/shared/Icon';
import { DEFAULT_AUTO_HIDE_TIMEOUT } from '../engagement/constants';
import { notificationPropType } from './notificationPropType';
import NotificationLinkedContent from './NotificationLinkedContent';
import useStyles from './useStyles';
import useLinkedContentVisiblity from './useLinkedContentVisibility';

const getAppointmentNotification = notification => {
  return {
    ...notification,
    icon: Time
  };
};
const NOTIFICATION_ALERT_LABEL = 'notification-alert-label';

/**
 * Component to display in app banner component for notifications
 * In top right in desktop
 * In bottom center in mobile
 */
const NotificationAlert = ({
  toastRef,
  testID,
  displayPosition,
  cursor,
  notificationsData,
  onDismiss,
  onSettingsClick,
  onAlertClick,
  eventId,
  autoHideTimeout,
  linkedContentStyles,
  headingText,
  children,
  surveyUrl,
  iconAriaLabel
}) => {
  const notificationsLength = notificationsData.length;
  const isApptNotification =
    notificationsLength === 1 &&
    notificationsData[0].channelName &&
    isApptNotificationChannel(notificationsData[0].channelName);
  const styles = useStyles(
    displayPosition,
    cursor,
    isApptNotification && !notificationsData[0].linkedContent
  );
  const { translate } = useTranslate();
  const { font, animation } = useTheme();
  const { hideIllustrations } = useThemeValues();
  const { isMobile } = useBreakpoints();
  const { isLinkedContentVisible } = useLinkedContentVisiblity();
  const animationOutDuration = parseInt(
    animation.delay.slowest.replace(/\D/g, ''),
    10
  );
  const [hasAlertMounted, setHasAlertMounted] = useState(true);
  const [iconColor, setIconColor] = useState(font.color.soft);
  const linkedContent = notificationsData[0]?.linkedContent || {};
  const { href, name } = linkedContent.content || {};
  const { contentType } = linkedContent || {};
  const updatedNotificationsData = isApptNotification
    ? [getAppointmentNotification(notificationsData[0])]
    : notificationsData;
  /* More than one notification can be shown at once, so this ensure unique landmark rule is not violated */
  const notificationAlertLabelId =
    notificationsData[0]?.id || NOTIFICATION_ALERT_LABEL;
  const displayContent =
    notificationsLength > 1 ? (
      <div
        {...injectTestId(`${testID}-heads-up-text`)}
        css={styles.notificationAlertWelcome}
      >
        <div css={styles.notificationAlertWelcomeTextContainer}>
          {translate('Notification-Alert-Welcome-Text')}
        </div>
        <div
          id={notificationAlertLabelId}
          {...injectTestId(`${testID}-text`)}
          css={styles.notificationAlertTextContainer}
          role="alert"
        >
          {translate('Notification-Alert-Display-Text', {
            count: notificationsLength
          })}
        </div>
      </div>
    ) : (
      <div
        id={notificationAlertLabelId}
        {...injectTestId(`${testID}-text`)}
        css={styles.notificationAlertTextContainer}
        aria-live="polite"
        aria-label={translate('Notification-Alert-Accessibility-Text', {
          body:
            updatedNotificationsData[0]?.renderBody?.() ||
            updatedNotificationsData[0]?.body
        })}
        role="alert"
      >
        <div aria-hidden="true">
          {updatedNotificationsData[0]?.renderBody?.() ||
            updatedNotificationsData[0]?.body}
        </div>
      </div>
    );

  const dismissButton = (
    <button
      css={styles.notificationAlertCancel}
      onClick={evt => {
        evt.preventDefault();
        evt.stopPropagation();
        setHasAlertMounted(false);
        onDismiss();
      }}
      onMouseEnter={() => setIconColor(font.color.hover)}
      onMouseLeave={() => setIconColor(font.color.soft)}
      {...injectTestId(`${testID}-cancel`)}
      aria-label={translate(
        'Notification-Alert-Dismiss-Button-Accessibility-Label'
      )}
    >
      <Icon name="close" size="sm" color={iconColor} aria-hidden />
    </button>
  );
  const childrenContainer = (
    <>
      <div css={styles.borderTop}></div>
      {headingText && (
        <h5
          css={styles.notificationHeading}
          {...injectTestId(`${testID}-heading`)}
        >
          {headingText}
        </h5>
      )}
      <div css={styles.notificationAlertCancelContainer}>{dismissButton}</div>
      <div
        css={styles.notificationContent}
        {...injectTestId(`${testID}-content`)}
      >
        {!hideIllustrations &&
          updatedNotificationsData[0]?.icon &&
          !updatedNotificationsData[0]?.image && (
            <div css={styles.notificationAlertLeftImageContainer}>
              <Image
                ariaHidden={iconAriaLabel}
                src={updatedNotificationsData[0]?.icon}
                style={styles.icon}
                {...injectTestId(`${testID}-icon`)}
              />
            </div>
          )}
        {updatedNotificationsData[0]?.image && (
          <div
            css={styles.notificationAlertLeftImageContainer}
            {...injectTestId(`${testID}-image`)}
          >
            {isValidElement(updatedNotificationsData[0]?.image) &&
              updatedNotificationsData[0]?.image}
          </div>
        )}
        <div>
          <div css={styles.notificationRightContainer}>
            {!children ? (
              <div css={styles.notificationAlertHeader}>
                {displayContent}
                <div css={styles.linkContentContainer}>
                  {linkedContent &&
                    (href || contentType === 'LinkedCallback') &&
                    updatedNotificationsData.length < 2 && (
                      <div
                        css={
                          linkedContentStyles ||
                          styles.linkContentInnerContainer
                        }
                      >
                        <TextLink
                          css={
                            href ? styles.notificationAlertLinkedContent : null
                          }
                          bold
                          size="m"
                          element={href ? 'a' : 'button'}
                          href={href}
                          target={href ? '_blank' : null}
                          rel={href ? 'noopener noreferrer' : null}
                          icon={
                            !linkedContentStyles ? (
                              <Icon
                                name="external-link"
                                size="xs"
                                color={font.color.soft}
                                aria-hidden
                              />
                            ) : null
                          }
                          aria-label={
                            href
                              ? translate('Universal-Link-Opens-New-Tab', {
                                  href
                                })
                              : null
                          }
                          testID={`${testID}-weburl-link`}
                        >
                          {name || href}
                        </TextLink>
                      </div>
                    )}
                </div>
              </div>
            ) : (
              children
            )}
          </div>
          {/* Eventual footer content goes here */}
          {/* <div
            {...injectTestId(`${testID}-footer`)}
            css={styles.notificationAlertFooter}
          >
          </div> */}
          {!isMobile && (
            <div css={styles.notificationAlertSettingsContainer}>
              <Link
                href={`/events/${eventId}/settings?tabId=notifications`}
                passHref
                target="_blank"
                {...injectTestId(`${testID}-settings`)}
                onClick={e => {
                  e.stopPropagation();
                  if (onSettingsClick) {
                    onSettingsClick();
                  }
                }}
                aria-label={translate('Notification-Settings')}
                css={styles.notificationAlertSettings}
              >
                <Icon
                  name="settings"
                  size="sm"
                  color={font.color.soft}
                  aria-hidden
                />
              </Link>
            </div>
          )}
        </div>
      </div>
    </>
  );

  // auto-hide
  useEffect(() => {
    if (autoHideTimeout > 0 || autoHideTimeout === undefined) {
      const timerId = setTimeout(() => {
        setHasAlertMounted(false);
      }, autoHideTimeout || DEFAULT_AUTO_HIDE_TIMEOUT);
      return () => {
        clearTimeout(timerId);
      };
    }
    return () => {};
  }, []);

  // fade-out animation
  useEffect(() => {
    let timerId;
    if (!hasAlertMounted) {
      timerId = setTimeout(() => {
        if (typeof onDismiss === 'function') onDismiss();
      }, animationOutDuration);
      return () => {
        clearTimeout(timerId);
      };
    }
    return () => {};
  }, [hasAlertMounted, onDismiss, animationOutDuration]);

  if (
    notificationsLength === 1 &&
    updatedNotificationsData[0].linkedContent &&
    isLinkedContentVisible(
      updatedNotificationsData[0].linkedContent.content?.type,
      updatedNotificationsData[0].linkedContent.content?.id
    )
  ) {
    return (
      <NotificationLinkedContent
        toastRef={toastRef}
        notification={updatedNotificationsData[0]}
        styles={[
          styles.notificationAlertContainer,
          hasAlertMounted ? {} : styles.fadeEffect
        ]}
        testID={testID}
        onClick={onAlertClick}
        eventId={eventId}
        surveyUrl={surveyUrl}
        ariaLabelledBy={notificationAlertLabelId}
      >
        {childrenContainer}
      </NotificationLinkedContent>
    );
  }

  return (
    <div
      ref={toastRef}
      role="button"
      tabIndex={0}
      css={[
        styles.notificationAlertContainer,
        hasAlertMounted ? {} : styles.fadeEffect
      ]}
      {...injectTestId(`${testID}-container`)}
      onClick={onAlertClick}
      onKeyDown={event => {
        if (event.key === 'Enter') {
          onAlertClick();
        }
      }}
    >
      {childrenContainer}
    </div>
  );
};

NotificationAlert.propTypes = {
  testID: PropTypes.string,
  toastRef: PropTypes.any,
  /** The position at which the component should be rendered */
  displayPosition: PropTypes.object,
  /** List of data for which component will be displayed */
  notificationsData: PropTypes.arrayOf(PropTypes.shape(notificationPropType))
    .isRequired,
  onDismiss: PropTypes.func,
  onSettingsClick: PropTypes.func,
  onAlertClick: PropTypes.func,
  eventId: PropTypes.string,
  // Provides a non default auto hide time - Value of 0 will not auto hide the message
  autoHideTimeout: PropTypes.number,
  // Optionally provide styles for the linkedContent
  linkedContentStyles: PropTypes.object,
  headingText: PropTypes.string,
  children: PropTypes.node,
  surveyUrl: PropTypes.string,
  // Overwrites cursor style for notificationRightContainer
  cursor: PropTypes.string,
  // Hides the icon from screen readers
  iconAriaLabel: PropTypes.bool
};

NotificationAlert.defaultProps = {
  testID: 'notification-alert'
};

export default NotificationAlert;
